/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import type { FormikProps } from 'formik';
import { TextV2, FormControl, InputV2, SpacingStack, Divider, Flex } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import type { Contacts, ContactInputFields, FieldVisibility } from '@apps/guest/contactCollector/shared/types';
import type { IframeContacts, IframeContactInputFields } from '@apps/guest/contactCollector/shared/types';
import { StyledFormControl, StyledContainer } from './ContactInfo.styles';
import { LazyNameAffixField } from '@shared/components/NameAffixField/NameAffixField.lazy';
import type { NameAffix, NameAffixSuggestionsState } from '@shared/components/NameAffixField/NameAffixField.types';
import { bindNameAffixField } from '@shared/components/NameAffixField/bindNameAffixField';
import { RemoveMember } from '../RemoveMember/RemoveMember';
import { IntlTelInputWrapper } from '@shared/components/IntlPhoneNumber/IntlTelInputWrapper';
import { useId } from '@withjoy/joykit/hooks';

interface ContactInfoProps
  extends Readonly<{
    formik: FormikProps<Contacts> | FormikProps<IframeContacts>;
    index: number;
    errors: {
      firstNameError: (index: number) => any;
      lastNameError: (index: number) => any;
      emailError: (index: number) => any;
    };
    isEmailInvalid: (index: number) => boolean;
    remove: (index: number) => ContactInputFields | IframeContactInputFields | undefined;
    isIframeVersion?: boolean;
    phoneNumberRule?: FieldVisibility;
    emailRule?: FieldVisibility;
    isRegistration?: boolean;
    /**
     * Renders the optional Title (prefix) / Suffix inputs. Off by default so the
     * other reuses of this form (registration, website-embedded forms, admin
     * guest list) stay unchanged.
     */
    showNameAffixFields?: boolean;
    /** Locale-keyed suggestions for the Title/Suffix fields, queried once at Form level. */
    nameAffixSuggestions?: NameAffixSuggestionsState;
    isPhoneNumberInvalidWithoutFocus: (index: number) => boolean;
    setIsPhoneNumberValid: React.Dispatch<React.SetStateAction<{ [key: number]: boolean }>>;
    phoneNumberTouched: { [key: number]: boolean };
    setPhoneNumberTouched: React.Dispatch<React.SetStateAction<{ [key: number]: boolean }>>;
    phoneInputResetKey?: number;
    phoneCountryCode?: string;
  }> {}

export const ContactInfo: React.FC<ContactInfoProps> = ({
  formik,
  index,
  errors: { firstNameError, lastNameError, emailError },
  isEmailInvalid,
  remove,
  isIframeVersion = false,
  phoneNumberRule = 'optional',
  emailRule = 'required',
  isRegistration = false,
  showNameAffixFields = false,
  nameAffixSuggestions,
  isPhoneNumberInvalidWithoutFocus,
  setIsPhoneNumberValid,
  phoneNumberTouched,
  setPhoneNumberTouched,
  phoneInputResetKey,
  phoneCountryCode = 'us',
}) => {
  const { t, tq } = useTranslation('contactCollector');
  const tAdmin = t('admin');
  const showRequiredMark = isIframeVersion && index === 0;
  // Each field gets an id, which its FormControl uses for the label's htmlFor, so the label names the field.
  const fieldId = `${useId(undefined, 'contact')}-${index}`;

  const nameAffixFieldProps = (affix: NameAffix) => bindNameAffixField(formik, `contacts[${index}].${affix}`, affix, nameAffixSuggestions);

  return (
    <StyledContainer spacing={isRegistration ? 5 : 6}>
      {/* Registration asks for contact details on a step of its own, where the fields say what they are. */}
      {index === 0 && !isRegistration && (
        <TextV2
          typographyVariant="hed4"
          marginTop="2rem"
          width="100%"
        >
          {tAdmin.contactInfo()}
        </TextV2>
      )}
      {index > 0 && <Divider marginTop="2rem" />}
      {index > 0 && (
        <Flex
          alignItems="center"
          gap={2}
          marginTop="24px"
        >
          {!formik.values.contacts[index]._prefilledContactId && (
            <RemoveMember
              formik={formik}
              remove={remove}
              index={index}
            />
          )}
          <TextV2 typographyVariant="hed4">{tq`Guest ${index + 1}`}</TextV2>
        </Flex>
      )}
      <Flex
        justifyContent="flex-start"
        columnGap="10px"
        marginTop={index > 0 ? '12px' : 0}
      >
        <SpacingStack
          spacing={6}
          width="100%"
        >
          {showNameAffixFields && <LazyNameAffixField {...nameAffixFieldProps('prefix')} />}
          <FormControl
            label={showRequiredMark ? tq`First Name*` : tq`First Name`}
            isInvalid={!!firstNameError(index)}
            error={firstNameError(index)}
            isRequired
          >
            <InputV2
              id={`${fieldId}-firstName`}
              {...formik.getFieldProps(`contacts[${index}].firstName`)}
              autoComplete="given-name"
            />
          </FormControl>
          <FormControl
            label={showRequiredMark ? tq`Last Name*` : tq`Last Name`}
            isInvalid={!!lastNameError(index)}
            error={lastNameError(index)}
            isRequired
          >
            <InputV2
              id={`${fieldId}-lastName`}
              {...formik.getFieldProps(`contacts[${index}].lastName`)}
              autoComplete="family-name"
            />
          </FormControl>
          {showNameAffixFields && <LazyNameAffixField {...nameAffixFieldProps('suffix')} />}
          {phoneNumberRule !== 'hidden' && (
            <StyledFormControl
              htmlFor={`${fieldId}-phone`}
              label={showRequiredMark && phoneNumberRule === 'required' ? tq`Mobile Phone*` : tq`Mobile Phone`}
              isInvalid={isPhoneNumberInvalidWithoutFocus(index)}
              error={isPhoneNumberInvalidWithoutFocus(index) ? tAdmin.invalidPhoneNumber() : ''}
              isRequired={phoneNumberRule === 'required' && index === 0}
            >
              <IntlTelInputWrapper
                resetKey={phoneInputResetKey}
                initialValue={formik.getFieldProps(`contacts[${index}].phoneNumber`).value}
                onChangeNumber={number => formik.setFieldValue(`contacts[${index}].phoneNumber`, number)}
                onChangeValidity={valid =>
                  setIsPhoneNumberValid(prevState => ({
                    ...prevState,
                    [index]: valid,
                  }))
                }
                initOptions={{
                  initialCountry: phoneCountryCode,
                  countryOrder: Array.from(new Set([phoneCountryCode, 'us', 'ca', 'gb', 'au'])),
                  formatOnDisplay: true,
                  strictMode: true,
                  useFullscreenPopup: false,
                }}
                inputProps={{
                  id: `${fieldId}-phone`,
                  autoComplete: 'tel',
                  onBlur: () => {
                    if (!phoneNumberTouched[index]) {
                      setPhoneNumberTouched(prevState => ({ ...prevState, [index]: true }));
                    }
                  },
                }}
              />
            </StyledFormControl>
          )}
          {emailRule !== 'hidden' && (
            <FormControl
              label={isRegistration || (showRequiredMark && emailRule === 'required') ? tq`Email*` : tq`Email`}
              isInvalid={(!isIframeVersion && isEmailInvalid(index)) || !!emailError(index)}
              error={!isIframeVersion && isEmailInvalid(index) ? tAdmin.required() : emailError(index) ? emailError(index) : ''}
              isRequired={!isIframeVersion && emailRule === 'required' && index === 0}
            >
              <InputV2
                id={`${fieldId}-email`}
                {...formik.getFieldProps(`contacts[${index}].email`)}
                placeholder={tq`your@email.com`}
                type="email"
                inputMode="email"
                autoComplete="email"
              />
            </FormControl>
          )}
        </SpacingStack>
      </Flex>
    </StyledContainer>
  );
};
